import React, { useState, useRef } from "react";
import { toast } from "react-toastify";
import "../styles/ReferralTab.css";

const ReferralTab = ({ user }) => {
  const [linkCopied, setLinkCopied] = useState(false);
  const [templateCopied, setTemplateCopied] = useState(false);
  const linkRef = useRef(null);
  const templateRef = useRef(null);

  // Create referral link based on user's email or ID
  const userIdentifier =
    user.username || encodeURIComponent(user.email.split("@")[0]);
  const referralLink = `https://www.expensebot.ai/signup?ref=${userIdentifier}`;

  // Email template with user's email and referral link
  const emailTemplate = `Subject: Simplify Your Expense Tracking - Special Invite from Your Accountant

Hi [Client Name],

I wanted to introduce you to ExpenseBot, a tool that many of my clients are now using to make expense tracking much easier.

ExpenseBot will help you:
- Automatically scan and categorize receipts
- Track expenses on the go
- Create professional expense reports
- Share reports directly with me for tax preparation

You can sign up using my referral link: ${referralLink}

When sharing your reports in ExpenseBot, please add my email: ${user.email}

Let me know if you have any questions!

Best regards,
[Your Name]`;

  // Function to copy referral link
  const copyReferralLink = () => {
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(referralLink)
        .then(() => {
          setLinkCopied(true);
          toast.success("Referral link copied to clipboard!");
          setTimeout(() => setLinkCopied(false), 3000);
        })
        .catch((err) => {
          console.error("Failed to copy link: ", err);
          toast.error(
            "Failed to copy link. Please try manually selecting and copying."
          );
        });
    } else {
      // Fallback for browsers without clipboard API
      if (linkRef.current) {
        linkRef.current.select();
        document.execCommand("copy");
        setLinkCopied(true);
        toast.success("Referral link copied to clipboard!");
        setTimeout(() => setLinkCopied(false), 3000);
      }
    }
  };

  // Function to copy email template
  const copyEmailTemplate = () => {
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(emailTemplate)
        .then(() => {
          setTemplateCopied(true);
          toast.success("Email template copied to clipboard!");
          setTimeout(() => setTemplateCopied(false), 3000);
        })
        .catch((err) => {
          console.error("Failed to copy template: ", err);
          toast.error(
            "Failed to copy template. Please try manually selecting and copying."
          );
        });
    } else {
      // Fallback for browsers without clipboard API
      if (templateRef.current) {
        templateRef.current.select();
        document.execCommand("copy");
        setTemplateCopied(true);
        toast.success("Email template copied to clipboard!");
        setTimeout(() => setTemplateCopied(false), 3000);
      }
    }
  };

  return (
    <div className="referral-tab-visual">
      <div className="referral-container">
        {/* Referral Card Section */}
        <div className="referral-card">
          <h2 className="referral-title">Share</h2>

          <div className="step-container">
            <div className="step-number">1</div>
            <div className="step-content">
              <h3 className="step-heading">Your Link</h3>
              <p className="step-desc">
                Copy your unique referral link and send it to clients
              </p>

              <div className="link-container">
                <input
                  ref={linkRef}
                  type="text"
                  value={referralLink}
                  readOnly
                  className="referral-link-input"
                  onClick={() => linkRef.current.select()}
                />
                <button
                  className={`referral-copy-btn ${linkCopied ? "copied" : ""}`}
                  onClick={copyReferralLink}
                >
                  <i className="fas fa-copy"></i> Copy
                </button>
              </div>
            </div>
          </div>

          <div className="referral-divider"></div>

          <div className="step-container">
            <div className="step-number">2</div>
            <div className="step-content">
              <h3 className="step-heading">Tell Clients To Share With You</h3>
              <p className="step-desc">
                When they use ExpenseBot, they'll share reports to:
              </p>

              <div className="email-display">
                <i className="fas fa-envelope"></i>
                <span className="user-email">{user.email}</span>
              </div>
            </div>
          </div>

          <div className="referral-divider"></div>

          <div className="step-container">
            <div className="step-number">3</div>
            <div className="step-content">
              <h3 className="step-heading">Earn Rewards</h3>
              <p className="step-desc">Earn from every referral</p>

              <div className="rewards-display">
                <div className="reward-icon">
                  <i className="fas fa-gift"></i>
                </div>
                <p className="reward-text">
                  <span className="reward-highlight">Earn</span> when clients
                  sign up.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="benefits-card">
          <h2 className="benefits-title">Benefits for Everyone</h2>

          <div className="benefits-grid">
            {/* Client Benefits */}
            <div className="benefit-item">
              <div className="benefit-icon">
                <i className="fas fa-bolt"></i>
              </div>
              <div className="benefit-content">
                <h3 className="benefit-heading">Snap & Submit</h3>
                <p className="benefit-desc">
                  Take a photo and AI handles the rest
                </p>
              </div>
            </div>

            <div className="benefit-item">
              <div className="benefit-icon">
                <i className="fas fa-tag"></i>
              </div>
              <div className="benefit-content">
                <h3 className="benefit-heading">Smart Tagging</h3>
                <p className="benefit-desc">
                  Easy categorization for different purposes
                </p>
              </div>
            </div>

            <div className="benefit-item">
              <div className="benefit-icon">
                <i className="fas fa-share-alt"></i>
              </div>
              <div className="benefit-content">
                <h3 className="benefit-heading">Seamless Sharing</h3>
                <p className="benefit-desc">
                  One-click sharing with finance team
                </p>
              </div>
            </div>

            {/* Accountant Benefits */}
            <div className="benefit-item">
              <div className="benefit-icon">
                <i className="fas fa-sync"></i>
              </div>
              <div className="benefit-content">
                <h3 className="benefit-heading">QuickBooks Integration</h3>
                <p className="benefit-desc">
                  One-click sync with accounting software
                </p>
              </div>
            </div>

            <div className="benefit-item">
              <div className="benefit-icon">
                <i className="fas fa-shield-alt"></i>
              </div>
              <div className="benefit-content">
                <h3 className="benefit-heading">AI Compliance Review</h3>
                <p className="benefit-desc">
                  Automatic checks against policies
                </p>
              </div>
            </div>

            <div className="benefit-item">
              <div className="benefit-icon">
                <i className="fas fa-comments"></i>
              </div>
              <div className="benefit-content">
                <h3 className="benefit-heading">Built-in Comments</h3>
                <p className="benefit-desc">
                  Discuss expenses in the spreadsheet
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Email Template Section */}
        <div className="template-card">
          <h2 className="template-title">Email Template</h2>

          <div className="template-container">
            <div className="template-header">
              <div className="template-icon">
                <i className="fas fa-envelope-open-text"></i>
              </div>
              <h3 className="template-heading">Client Invitation</h3>
              <button
                className={`template-copy-btn ${
                  templateCopied ? "copied" : ""
                }`}
                onClick={copyEmailTemplate}
              >
                <i className="fas fa-copy"></i> Copy
              </button>
            </div>

            <div className="template-body">
              <p>
                <strong>Subject:</strong> Simplify Your Expense Tracking -
                Special Invite
              </p>
              <br />
              <p>Hi [Client Name],</p>
              <br />
              <p>
                I wanted to introduce you to ExpenseBot, a tool that many of my
                clients use to make expense tracking easier.
              </p>
              <br />
              <p>ExpenseBot will help you:</p>
              <ul>
                <li>Automatically scan and categorize receipts</li>
                <li>Track expenses on the go</li>
                <li>Create professional expense reports</li>
                <li>Share reports directly with me</li>
              </ul>
              <br />
              <p>
                Sign up using my link:{" "}
                <span className="template-link">{referralLink}</span>
              </p>
              <br />
              <p>
                Add my email when sharing: <strong>{user.email}</strong>
              </p>
              <br />
              <p>
                Best regards,
                <br />
                [Your Name]
              </p>
            </div>
          </div>
        </div>

        {/* Hidden textarea for copy fallback */}
        <textarea
          ref={templateRef}
          value={emailTemplate}
          style={{ position: "absolute", left: "-9999px" }}
          readOnly
        />
      </div>
    </div>
  );
};

export default ReferralTab;
